import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Link,
  Stack,
  Chip,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridSortModel,
  GridColumnVisibilityModel,
  GridRowParams,
  GridRowId,
  GridRowSelectionModel,
  GridToolbar,
} from "@mui/x-data-grid";
import DeleteIcon from "@mui/icons-material/Delete";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { IoLogoInstagram, IoLogoTwitter, IoLogoYoutube } from "react-icons/io5";
import { SiTiktok, SiFacebook } from "react-icons/si";
import LanguageIcon from "@mui/icons-material/Language";
import LinkIcon from "@mui/icons-material/Link";
import {
  ReactionChannel,
  PlaylistingChannel,
  SpotifyPlaylist,
  MusicBlog,
  OutreachCategory,
  SocialLink,
  SocialPlatform,
} from "../../types/outreach";
import { ChannelEditModal } from "./ChannelEditModal";
import { SpotifyPlaylistEditModal } from "./SpotifyPlaylistEditModal";
import { resolveSocialLinks } from "../../utils/socialLinks";

// Utility function to format numbers with commas and abbreviations
export const formatNumber = (num: any): string => {
  // If the value is undefined, null, empty string, or not provided, return the original value
  if (num === undefined || num === null || num === "") {
    return String(num);
  }

  // Convert to number if it's a string
  let value: number;
  try {
    value = typeof num === "string" ? parseFloat(num) : Number(num);
  } catch (e) {
    // If conversion fails, return the original value
    return String(num);
  }

  // Check if it's a valid number
  if (isNaN(value)) {
    return String(num);
  }

  // Format with abbreviations
  if (value >= 1000000000) {
    return (value / 1000000000).toFixed(1).replace(/\.0$/, "") + "B";
  } else if (value >= 1000000) {
    return (value / 1000000).toFixed(1).replace(/\.0$/, "") + "M";
  } else if (value >= 1000) {
    return (value / 1000).toFixed(1).replace(/\.0$/, "") + "K";
  } else {
    return value.toLocaleString();
  }
};

// Utility function to parse numeric values from various types
const parseNumericValue = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string") {
    const normalized = value.replace(/[^0-9+\-\.]/g, "");
    if (!normalized) {
      return null;
    }
    const parsed = Number(normalized);
    return Number.isNaN(parsed) ? null : parsed;
  }
  return null;
};

// Calculate engagement rate from row data
const calculateEngagementRate = (row: Record<string, any>): number | null => {
  if (!row) {
    return null;
  }

  // Check if there's a direct engagement value first
  const directValue = parseNumericValue(row.engagement);
  if (directValue !== null) {
    return directValue;
  }

  // Calculate from likes and subscriber count
  const likes = parseNumericValue(row.likes) ?? 0;
  const subscribers = parseNumericValue(row.subscriberCount) ?? 0;

  if (subscribers === 0) {
    return null;
  }

  return (likes / subscribers) * 100;
};

// Format engagement rate for display
const formatEngagementRate = (rate: number | null): string => {
  if (rate === null) {
    return "N/A";
  }
  return `${rate.toFixed(2)}%`;
};

// Get comparable engagement rate for sorting (returns -1 for null values)
const getComparableEngagementRate = (row: Record<string, any>): number => {
  const rate = calculateEngagementRate(row);
  return rate === null ? -1 : rate;
};

interface OutreachDataGridProps {
  category: OutreachCategory;
  data: any[];
  loading: boolean;
  onEdit: (item: any) => void;
  onDelete: (id: string) => void;
  columnVisibility?: GridColumnVisibilityModel;
  onColumnVisibilityChange?: (model: GridColumnVisibilityModel) => void;
  sortModel?: GridSortModel;
  onSortModelChange?: (model: GridSortModel) => void;
  rowSelectionModel?: GridRowSelectionModel;
  onRowSelectionModelChange?: (
    rowSelectionModel: GridRowSelectionModel
  ) => void;
  // Added props
  checkboxSelection?: boolean;
  customColumns?: GridColDef[];
  onRowClick?: (params: GridRowParams) => void;
  selectedRowId?: GridRowId;
  onUpdateChannels?: (
    ids: string[],
    updates: Record<string, any>
  ) => Promise<void>;
}

export const OutreachDataGrid: React.FC<OutreachDataGridProps> = ({
  category,
  data,
  loading,
  onEdit,
  onDelete,
  columnVisibility,
  onColumnVisibilityChange,
  sortModel,
  onSortModelChange,
  rowSelectionModel,
  onRowSelectionModelChange,
  checkboxSelection,
  customColumns,
  onRowClick,
  selectedRowId,
  onUpdateChannels,
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md")); // 768px and below
  const isChannelCategory =
    category === OutreachCategory.REACTION_CHANNELS ||
    category === OutreachCategory.PLAYLISTING_CHANNELS;
  const isSpotifyCategory = category === OutreachCategory.SPOTIFY_PLAYLISTS;

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [channelsForEdit, setChannelsForEdit] = useState<
    (ReactionChannel | PlaylistingChannel)[]
  >([]);
  const [playlistsForEdit, setPlaylistsForEdit] = useState<SpotifyPlaylist[]>([]);

  const selectedIds = useMemo(
    () => (rowSelectionModel ?? []).map((rowId) => rowId.toString()),
    [rowSelectionModel]
  );

  const selectedChannels = useMemo(() => {
    if (!isChannelCategory) {
      return [] as (ReactionChannel | PlaylistingChannel)[];
    }

    const selectedSet = new Set(selectedIds);
    return data.filter((item) => selectedSet.has(item.id)) as (
      | ReactionChannel
      | PlaylistingChannel
    )[];
  }, [data, selectedIds, isChannelCategory]);

  const selectedPlaylists = useMemo(() => {
    if (!isSpotifyCategory) {
      return [] as SpotifyPlaylist[];
    }

    const selectedSet = new Set(selectedIds);
    return data.filter((item) => selectedSet.has(item.id)) as SpotifyPlaylist[];
  }, [data, selectedIds, isSpotifyCategory]);

  const handleOpenEditModal = () => {
    if (isChannelCategory && selectedChannels.length > 0) {
      setEditError(null);
      setChannelsForEdit(selectedChannels);
      setPlaylistsForEdit([]);
      setEditModalOpen(true);
      return;
    }

    if (isSpotifyCategory && selectedPlaylists.length > 0) {
      setEditError(null);
      setChannelsForEdit([]);
      setPlaylistsForEdit(selectedPlaylists);
      setEditModalOpen(true);
    }
  };

  const handleCloseEditModal = () => {
    if (editSaving) {
      return;
    }
    setEditModalOpen(false);
    setChannelsForEdit([]);
    setPlaylistsForEdit([]);
    setEditError(null);
  };

  const handleSaveSelection = async (updates: Record<string, any>) => {
    if (!onUpdateChannels) {
      return;
    }

    const ids =
      channelsForEdit.length > 0
        ? channelsForEdit.map((channel) => channel.id)
        : playlistsForEdit.map((playlist) => playlist.id);
    if (ids.length === 0) {
      return;
    }

    setEditSaving(true);
    setEditError(null);

    try {
      await onUpdateChannels(ids, updates);
      setEditModalOpen(false);
      setChannelsForEdit([]);
      setPlaylistsForEdit([]);
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to update the selected items.";
      setEditError(message);
    } finally {
      setEditSaving(false);
    }
  };

  const showEditButton =
    typeof onUpdateChannels === "function" &&
    ((isChannelCategory && selectedChannels.length > 0) ||
      (isSpotifyCategory && selectedPlaylists.length > 0));

  const editButtonLabel = (() => {
    if (isSpotifyCategory) {
      return `Edit Playlist${selectedPlaylists.length > 1 ? "s" : ""}`;
    }

    return `Edit Channel${selectedChannels.length > 1 ? "s" : ""}`;
  })();

  // Handle delete button click - directly delete without confirmation
  const handleDeleteClick = (item: any) => {
    onDelete(item.id);
  };

  // Render URL as a clickable link
  const renderUrlCell = (params: GridRenderCellParams) => {
    const value = params.value as string;
    return (
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Link
          variant="body2"
          sx={{
            mr: 1,
            maxWidth: 200,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {value}
        </Link>
        {value && (
          <Tooltip title="Open link">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                window.open(value, "_blank");
              }}
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    );
  };

  // Render channel name as a clickable link using the channel URL
  const renderChannelNameCell = (params: GridRenderCellParams) => {
    if (!params || !params.row) return null;

    const name = params.value as string;
    const url = params.row.channelUrl as string;

    return (
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Link
          component="button"
          variant="body2"
          onClick={(e) => {
            e.stopPropagation();
            if (url) {
              window.open(url, "_blank");
            }
          }}
          sx={{
            textAlign: "left",
            width: 200,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            textDecoration: url ? "underline" : "none",
            cursor: url ? "pointer" : "default",
            color: url ? "primary.main" : "text.primary",
          }}
        >
          {name}
        </Link>
      </Box>
    );
  };

  const renderActionsCell = (params: GridRenderCellParams) => {
    return (
      <Box>
        <Tooltip title="Delete">
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteClick(params.row);
            }}
            size="small"
            color="error"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    );
  };

  // Render genre as chips
  const renderGenreCell = (params: GridRenderCellParams) => {
    const genres = (params.value as string)?.split("/");
    return (
      <Stack direction="row" spacing={1}>
        {genres?.map((genre) => (
          <Chip key={genre} label={genre} size="small" />
        ))}
      </Stack>
    );
  };

  // Render email as a mailto link
  const renderEmailCell = (params: GridRenderCellParams) => {
    const email = params.value as string;
    return email ? (
      <Link
        href={`mailto:${email}`}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          display: "block",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {email}
      </Link>
    ) : null;
  };

  // Render social media handles
  const socialPlatformIcons: Partial<Record<SocialPlatform, React.ReactNode>> = {
    instagram: <IoLogoInstagram />,
    twitter: <IoLogoTwitter />,
    youtube: <IoLogoYoutube />,
    tiktok: <SiTiktok />,
    facebook: <SiFacebook />,
    website: <LanguageIcon fontSize="small" />,
    other: <LinkIcon fontSize="small" />,
  };

  const renderSocialHandlesCell = (params: GridRenderCellParams) => {
    const row = params.row as {
      socialLinks?: SocialLink[];
      socialHandles?: string;
    };
    const legacyValue = (row?.socialHandles ?? params.value) as string | undefined;
    const links = resolveSocialLinks(row?.socialLinks, legacyValue);
    if (links.length === 0) {
      return null;
    }

    return (
      <table>
        <tbody>
          {links.map((link, index) => {
            const icon =
              socialPlatformIcons[link.platform] ?? (
                <LinkIcon fontSize="small" />
              );
            const label = link.label ?? link.handle;
            const key = `${link.platform}-${link.handle}-${index}`;

            return (
              <tr key={key}>
                <td className="social-media-table-cell">
                  {link.url ? (
                    <Link
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: "inline-block",
                        marginRight: "8px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {label}
                    </Link>
                  ) : (
                    label
                  )}
                </td>
                <td className="social-media-table-cell icon">{icon}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  // Render playlist name as a clickable link
  const renderPlaylistNameCell = (params: GridRenderCellParams) => {
    if (!params || !params.row) return null;

    const name = params.value as string;
    const url = params.row.playlistUrl as string;

    return (
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Link
          component="button"
          variant="body2"
          onClick={(e) => {
            e.stopPropagation();
            if (url) {
              window.open(url, "_blank");
            }
          }}
          sx={{
            textAlign: "left",
            maxWidth: 200,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            textDecoration: url ? "underline" : "none",
            cursor: url ? "pointer" : "default",
            color: url ? "primary.main" : "text.primary",
          }}
        >
          {name}
        </Link>
      </Box>
    );
  };

  // Render blog name as a clickable link
  const renderBlogNameCell = (params: GridRenderCellParams) => {
    if (!params || !params.row) return null;

    const name = params.value as string;
    const url = params.row.blogUrl as string;

    return (
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Link
          component="button"
          variant="body2"
          onClick={(e) => {
            e.stopPropagation();
            if (url) {
              window.open(url, "_blank");
            }
          }}
          sx={{
            textAlign: "left",
            maxWidth: 200,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            textDecoration: url ? "underline" : "none",
            cursor: url ? "pointer" : "default",
            color: url ? "primary.main" : "text.primary",
          }}
        >
          {name}
        </Link>
      </Box>
    );
  };

  // Render number with formatting
  const renderNumberCell = (params: GridRenderCellParams) => {
    return <span>{formatNumber(params.value)}</span>;
  };

  // Calculate and render engagement rate
  const renderEngagementRateCell = (params: GridRenderCellParams) => {
    const rate = calculateEngagementRate(params?.row ?? {});
    return <span>{formatEngagementRate(rate)}</span>;
  };

  // Column definitions for each category
  const getColumns = (): GridColDef[] => {
    const actionColumn = {
      field: "actions",
      headerName: "Actions",
      width: 120,
      renderCell: renderActionsCell,
      sortable: false,
      filterable: false,
    };

    switch (category) {
      case OutreachCategory.REACTION_CHANNELS:
        return [
          {
            field: "channelName",
            headerName: "Channel Name",
            width: 200,
            renderCell: renderChannelNameCell,
          },
          {
            field: "subscriberCount",
            headerName: "Subscriber Count",
            width: 150,
            type: "number",
            renderCell: renderNumberCell,
          },
          {
            field: "viewCount",
            headerName: "Total Views",
            width: 150,
            type: "number",
            renderCell: renderNumberCell,
          },
          {
            field: "videoCount",
            headerName: "Videos",
            width: 100,
            type: "number",
            renderCell: renderNumberCell,
          },
          {
            field: "likes",
            headerName: "Likes",
            width: 120,
            type: "number",
            renderCell: renderNumberCell,
          },
          {
            field: "engagement",
            headerName: "Engagement",
            width: 120,
            type: "number",
            renderCell: renderEngagementRateCell,
            valueGetter: (params: any) =>
              calculateEngagementRate(params?.row ?? {}) ?? undefined,
            sortComparator: (value1, value2, param1, param2) => {
              const row1 = param1.api.getRow(param1.id);
              const row2 = param2.api.getRow(param2.id);
              const rate1 = getComparableEngagementRate(row1 ?? {});
              const rate2 = getComparableEngagementRate(row2 ?? {});
              if (rate1 === rate2) {
                return 0;
              }
              return rate1 > rate2 ? 1 : -1;
            },
            description:
              "Engagement Rate = (Likes on Latest 50 Videos / Subscriber Count) × 100",
          },
          {
            field: "genre",
            headerName: "Genre",
            width: 150,
            renderCell: renderGenreCell,
          },
          {
            field: "email",
            headerName: "Email",
            flex: 1,
            minWidth: 200,
            renderCell: renderEmailCell,
          },
          {
            field: "socialHandles",
            headerName: "Social Media",
            flex: 1,
            minWidth: 200,
            renderCell: renderSocialHandlesCell,
          },
          actionColumn,
        ];
      case OutreachCategory.PLAYLISTING_CHANNELS:
        return [
          {
            field: "channelName",
            headerName: "Channel Name",
            width: 200,
            renderCell: renderChannelNameCell,
          },
          {
            field: "subscriberCount",
            headerName: "Subscriber Count",
            width: 150,
            type: "number",
            renderCell: renderNumberCell,
          },
          {
            field: "viewCount",
            headerName: "Total Views",
            width: 150,
            type: "number",
            renderCell: renderNumberCell,
          },
          {
            field: "videoCount",
            headerName: "Videos",
            width: 100,
            type: "number",
            renderCell: renderNumberCell,
          },
          {
            field: "likes",
            headerName: "Likes",
            width: 120,
            type: "number",
            renderCell: renderNumberCell,
          },
          {
            field: "engagement",
            headerName: "Engagement",
            width: 120,
            type: "number",
            renderCell: renderEngagementRateCell,
            valueGetter: (params: any) =>
              calculateEngagementRate(params?.row ?? {}) ?? undefined,
            sortComparator: (value1, value2, param1, param2) => {
              const row1 = param1.api.getRow(param1.id);
              const row2 = param2.api.getRow(param2.id);
              const rate1 = getComparableEngagementRate(row1 ?? {});
              const rate2 = getComparableEngagementRate(row2 ?? {});
              if (rate1 === rate2) {
                return 0;
              }
              return rate1 > rate2 ? 1 : -1;
            },
            description:
              "Engagement Rate = (Likes on Latest 50 Videos / Subscriber Count) × 100",
          },
          {
            field: "genre",
            headerName: "Genre",
            width: 250,
            renderCell: renderGenreCell,
          },
          {
            field: "email",
            headerName: "Email",
            flex: 1,
            minWidth: 200,
            renderCell: renderEmailCell,
          },
          actionColumn,
        ];
      case OutreachCategory.SPOTIFY_PLAYLISTS:
        return [
          {
            field: "playlistName",
            headerName: "Playlist Name",
            flex: 1,
            minWidth: 200,
            renderCell: renderPlaylistNameCell,
          },
          {
            field: "playlistLikes",
            headerName: "Likes",
            width: 120,
            type: "number",
            renderCell: renderNumberCell,
          },
          {
            field: "playlistFollowers",
            headerName: "Followers",
            width: 120,
            type: "number",
            renderCell: renderNumberCell,
          },
          {
            field: "trackCount",
            headerName: "Tracks",
            width: 100,
            type: "number",
            renderCell: renderNumberCell,
          },
          {
            field: "genre",
            headerName: "Genre",
            width: 150,
            renderCell: renderGenreCell,
          },
          {
            field: "curatorSocialHandle",
            headerName: "Social Media",
            flex: 1,
            width: 150,
            renderCell: renderSocialHandlesCell,
          },
          actionColumn,
        ];
      case OutreachCategory.MUSIC_BLOGS:
        return [
          {
            field: "blogName",
            headerName: "Blog",
            flex: 1,
            minWidth: 200,
            renderCell: renderBlogNameCell,
          },
          { field: "contact", headerName: "Contact", flex: 1, minWidth: 200 },
          { field: "genre", headerName: "Genre", width: 150 },
          {
            field: "monthlyVisits",
            headerName: "Monthly Visits",
            type: "number",
            width: 150,
            renderCell: renderNumberCell,
          },
          actionColumn,
        ];
      default:
        return [];
    }
  };

  // Get columns with custom columns prepended if provided
  const getAllColumns = (): GridColDef[] => {
    const standardColumns = getColumns();
    return customColumns
      ? [...customColumns, ...standardColumns]
      : standardColumns;
  };

  return (
    <Box sx={{ 
      position: "relative", 
      width: "100%", 
      height: "100%",
      display: 'flex',
      flexDirection: 'column',
      minHeight: 0,
    }}>
      {showEditButton && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1, flexShrink: 0 }}>
          <Button variant="contained" onClick={handleOpenEditModal}>
            {editButtonLabel}
          </Button>
        </Box>
      )}
      <DataGrid
        rows={data}
        columns={getAllColumns()}
        initialState={{
          pagination: {
            paginationModel: { page: 0, pageSize: 10 },
          },
          sorting: {
            sortModel: sortModel || [],
          },
        }}
        columnVisibilityModel={columnVisibility}
        onSortModelChange={onSortModelChange}
        onColumnVisibilityModelChange={onColumnVisibilityChange}
        pageSizeOptions={[10, 25, 50]}
        checkboxSelection={
          checkboxSelection !== undefined ? checkboxSelection : !isMobile
        }
        rowSelectionModel={rowSelectionModel ?? ([] as GridRowSelectionModel)}
        onRowSelectionModelChange={(newSelectionModel) => {
          if (onRowSelectionModelChange) {
            onRowSelectionModelChange(newSelectionModel);
          }
        }}
        onRowClick={onRowClick}
        getRowClassName={(params) =>
          selectedRowId !== undefined && params.id === selectedRowId
            ? "selected-recipient-row"
            : ""
        }
        loading={loading}
        slots={{ toolbar: GridToolbar }}
        slotProps={{
          toolbar: {
            showQuickFilter: true,
            quickFilterProps: { debounceMs: 500 },
          },
        }}
        sx={{
          flexGrow: 1,
          minHeight: 0,
          height: '100%',
          minWidth: 0, // Allow table to shrink below its content width
          overflow: "auto", // Enable horizontal scroll
          "& .MuiDataGrid-root": {
            minWidth: "max-content", // Ensure content doesn't get cut off
          },
          "& .MuiDataGrid-cell": {
            whiteSpace: "normal",
            wordWrap: "break-word",
            minWidth: 0, // Allow cells to shrink
          },
          "& .selected-recipient-row": {
            backgroundColor: theme.palette.action.selected,
            borderLeft: `4px solid ${theme.palette.primary.main}`,
            "&:hover": {
              backgroundColor: theme.palette.action.hover,
            },
            "&.Mui-selected": {
              backgroundColor: theme.palette.action.selected,
            },
            "&.Mui-selected:hover": {
              backgroundColor: theme.palette.action.hover,
            },
          },
          // Make headers sticky
          "& .MuiDataGrid-columnHeaders": {
            position: "sticky",
            top: 0,
            backgroundColor: "background.paper",
            zIndex: 1,
          },
          // Mobile-specific responsive adjustments
          "@media (max-width: 768px)": {
            "& .MuiDataGrid-columnsContainer": {
              minWidth: "max-content",
            },
            "& .MuiDataGrid-cell": {
              fontSize: "0.875rem", // Slightly smaller font on mobile
              padding: "4px 8px", // Reduce cell padding
            },
            "& .MuiDataGrid-columnHeader": {
              fontSize: "0.875rem",
              padding: "4px 8px",
            },
            // Hide checkbox selection on mobile to save space
            "& .MuiDataGrid-checkboxInput": {
              display: "none",
            },
            "& .MuiDataGrid-columnHeaderCheckbox": {
              display: "none",
            },
            "& .MuiDataGrid-cellCheckbox": {
              display: "none",
            },
          },
          // Extra small mobile devices
          "@media (max-width: 480px)": {
            "& .MuiDataGrid-cell": {
              fontSize: "0.75rem", // Even smaller font for very small screens
              padding: "2px 4px", // Minimal padding
            },
            "& .MuiDataGrid-columnHeader": {
              fontSize: "0.75rem",
              padding: "2px 4px",
            },
          },
          // Custom scrollbar styling
          "&::-webkit-scrollbar": {
            width: "8px",
            height: "8px",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "rgba(0, 0, 0, 0.05)",
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#48b5bd",
            borderRadius: "4px",
            "&:hover": {
              backgroundColor: "#3da1a9",
            },
          },
          // Apply scrollbar styling to the inner elements
          "& .MuiDataGrid-virtualScroller::-webkit-scrollbar": {
            width: "8px",
            height: "8px",
          },
          "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-track": {
            backgroundColor: "rgba(0, 0, 0, 0.05)",
            borderRadius: "4px",
          },
          "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb": {
            backgroundColor: "#48b5bd",
            borderRadius: "4px",
            "&:hover": {
              backgroundColor: "#3da1a9",
            },
          },
        }}
      />
      {isChannelCategory && onUpdateChannels && channelsForEdit.length > 0 && (
        <ChannelEditModal
          open={editModalOpen}
          category={category}
          channels={channelsForEdit}
          onClose={handleCloseEditModal}
          onSave={handleSaveSelection}
          saving={editSaving}
          error={editError}
        />
      )}
      {isSpotifyCategory && onUpdateChannels && playlistsForEdit.length > 0 && (
        <SpotifyPlaylistEditModal
          open={editModalOpen}
          playlists={playlistsForEdit}
          onClose={handleCloseEditModal}
          onSave={handleSaveSelection}
          saving={editSaving}
          error={editError}
        />
      )}
    </Box>
  );
};
